<template>
  <a-modal
    :visible="value"
    :title="isEditMode ? 'Edit widget' : 'Add widget'"
    width="700px"
    destroyOnClose
    @cancel="$emit('close')"
  >
    <template slot="default">
      <div class="widget-modal">
        <LanguageSelect
          v-model="selectedLanguage"
          :langs="langs"
          :contentFilled="filledLangs"
          :error="formErrorState"
          short-button-label
          @copyFromDefault="handleCopyFromDefault"
          @clear="handleClearLang"
        />
        <a-form-model :rules="rules" :model="currentForm" ref="widgetForm">
          <a-form-model-item
            prop="backgroundImageUrl"
            label="Background image"
            :colon="false"
          >
            <div class="image-uploader">
              <ImageUploader
                v-model="currentForm.backgroundImageUrl"
                :file-width="1160"
                :file-height="208"
                aspect-ratio="145 / 26"
                has-delete-overlay
                @change="handleImageUpload"
              />
            </div>
          </a-form-model-item>

          <a-form-model-item
            prop="name"
            :colon="false"
            required
            class="normal-size"
          >
            <template #label>
              <span class="title" style="line-height: 1">
                Name
                <span style="font-weight: 400; color: #8c8c8c">
                  (for internal usage)
                </span>
              </span>
              <a-tooltip slot="suffix" title="Visible only in admin panel">
                <a-icon type="info-circle" class="text-primary" />
              </a-tooltip>
            </template>
            <a-input
              v-model="currentForm.name"
              placeholder="Name"
              size="large"
            />
          </a-form-model-item>

          <div class="form-row">
            <a-form-model-item prop="linkType" :colon="false" label="Link type">
              <a-select
                v-model="currentForm.linkType"
                size="large"
                :disabled="loading"
              >
                <template #placeholder>
                  <span style="color: #a1a1c2">Select link type</span>
                </template>
                <a-select-option value="custom"> Custom </a-select-option>
                <a-select-option value="sport"> Sport </a-select-option>
                <a-select-option value="match"> Match </a-select-option>
              </a-select>
            </a-form-model-item>

            <div v-if="currentForm.linkType === 'custom'">
              <a-form-model-item prop="link" :colon="false">
                <template #label>
                  <span>Link</span>
                  <a-button
                    @click="openAddLinkModalModal"
                    :disabled="isLinkModalDisabled"
                    size="small"
                    type="link"
                    style="
                      padding-left: 8px !important;
                      line-height: 1.2;
                      height: auto;
                      border: none;
                    "
                  >
                    Save as a template
                  </a-button>
                </template>
                <a-select
                  :disabled="loading"
                  v-model="currentForm.link"
                  mode="tags"
                  optionLabelProp="label"
                  size="large"
                  @change="handleTemplateChange"
                >
                  <template #placeholder>
                    <span style="color: #a1a1c2">
                      https://www.olimp.bet/line/1/5956496/78487617
                    </span>
                  </template>
                  <a-select-option
                    v-for="linkTemplate in filteredLinkTemplates"
                    :key="String(linkTemplate.id)"
                    :value="String(linkTemplate.url)"
                    :label="linkTemplate.url"
                  >
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <span style="display: block">
                          {{ linkTemplate.name }}
                        </span>
                        <span style="color: gray; font-size: 12px">
                          {{
                            linkTemplate.url?.length > 60
                              ? linkTemplate.url?.slice(0, 60) + "..."
                              : linkTemplate.url
                          }}
                        </span>
                      </div>
                      <a-popconfirm
                        overlayClassName="deleteTemplateOverlay"
                        :disabled="false"
                        :getPopupContainer="getPopupContainer"
                        title="Sure to delete this template?"
                        @confirm="(e) => deleteTemplate(e, linkTemplate.id)"
                      >
                        <a-icon
                          class="text-danger delete-icon"
                          type="delete"
                          @click.stop
                        />
                      </a-popconfirm>
                    </div>
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </div>

            <a-form-model-item
              v-if="currentForm.linkType === 'match'"
              :colon="false"
              prop="link"
              label="Banner link"
              required
            >
              <a-input
                v-model="currentForm.link"
                :disabled="loading"
                size="large"
                placeholder="https://www.olimp.bet/line/1/5956496/78487617"
                style="height: 40px"
              >
              </a-input>
            </a-form-model-item>

            <a-form-model-item
              v-if="currentForm.linkType === 'sport'"
              :colon="false"
              prop="sportId"
              required
            >
              <template #label> Select sport </template>
              <a-select
                v-model="currentForm.sportId"
                size="large"
                :disabled="loading"
                allow-clear
              >
                <template #placeholder>
                  <span style="color: #a1a1c2">Select sport</span>
                </template>
                <a-select-option v-for="sport in sports" :key="sport.sport_id">
                  {{ sport.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </div>

          <a-form-model-item
            :colon="false"
            label="Show info icon"
            class="switch"
          >
            <a-switch v-model="currentForm.showInfo" size="small" />
          </a-form-model-item>

          <div v-if="currentForm.showInfo">
            <a-form-model-item :colon="false" label="Info link" prop="infoLink">
              <a-input
                v-model="currentForm.infoLink"
                size="large"
                placeholder="https://www.olimp.bet"
              />
            </a-form-model-item>
          </div>
        </a-form-model>
      </div>

      <a-modal
        v-model="isAddLinkModalOpened"
        width="395px"
        :destroyOnClose="true"
        :footer="null"
      >
        <template #title>
          <span class="modal-title">Add new template</span>
        </template>
        <TemplatesModal
          :templateLink="selectedLink?.link || ''"
          @addLink="handleAddTemplatesModal"
          @closeTemplateModal="() => (isAddLinkModalOpened = false)"
        />
      </a-modal>
    </template>
    <template slot="footer">
      <div class="footer">
        <a-button
          v-if="isEditMode"
          key="Delete"
          type="danger"
          @click="$emit('delete', editId)"
        >
          Delete
        </a-button>
        <div style="margin-left: auto">
          <a-button key="cancel" @click="$emit('close')"> Cancel </a-button>
          <a-button
            v-if="isEditMode"
            key="save"
            type="primary"
            @click="handleSubmit"
          >
            Save
          </a-button>
          <a-button v-else key="add" type="primary" @click="handleSubmit">
            Add
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>
<script>
import TemplatesModal from '@/components/custom/modals/linkTemplateModal.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import ImageUploader from '@/components/custom/imageUploader.vue'

import { validateURL } from '@/services/utils/validators'
import apiClient from '@/services/axios'

import { DEFAULT_LANG } from '@/constants/lang'

import { mapState, mapActions } from 'vuex'

import isEqual from 'lodash/isEqual'

const FORM_FIELDS = {
  backgroundImageUrl: '',
  backgroundImageFile: null,
  backgroundImageId: null,
  name: '',
  linkType: 'custom',
  link: [],
  sportId: null,
  showInfo: false,
  infoLink: '',
}

export default {
  props: {
    value: {
      type: Boolean,
      reqiured: true,
    },
    data: {
      type: Object,
    },
    initLang: {
      type: String,
      default: 'ru',
    },
  },
  components: {
    TemplatesModal,
    LanguageSelect,
    ImageUploader,
  },
  data() {
    return {
      form: {
        ru: { ...JSON.parse(JSON.stringify(FORM_FIELDS)) },
      },
      rules: {
        backgroundImageUrl: [
          {
            required: true,
            message: 'Please upload background image',
            trigger: 'change',
          },
        ],
        name: [
          { required: true, message: 'Please enter a name', trigger: 'blur' },
        ],
        linkType: [
          {
            required: true,
            message: 'Please select link type',
            trigger: 'change',
          },
        ],
        link: [
          { required: true, message: 'Please enter a link', trigger: 'blur' },
        ],
        sportId: [
          {
            required: true,
            message: 'Please select a sport',
            trigger: 'change',
          },
        ],
        infoLink: [
          {
            required: true,
            message: 'Please enter an info link',
            trigger: 'blur',
          },
          { validator: validateURL, trigger: 'blur' },
        ],
      },
      selectedLanguage: 'ru',
      linkTemplates: [],
      loading: false,
      isAddLinkModalOpened: false,
      selectedLink: null,
      validationResult: true,
    }
  },
  methods: {
    ...mapActions(['fetchSports']),
    createFields() {
      return { ...JSON.parse(JSON.stringify(FORM_FIELDS)) }
    },
    initForm() {
      if (this.langs.length === 0) {
        return {
          ru: this.createFields(),
        }
      }

      this.form = this.langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: this.createFields(),
        }
      }, {})
    },
    fillForm() {
      if (!this.data) {
        return
      }

      const clonedData = JSON.parse(JSON.stringify(this.data))
      this.form = { ...this.form, ...clonedData }

      if (!this.form.ru?.title) {
        Object.entries(this.form).forEach(([key, value]) => {
          if (JSON.stringify(value) !== JSON.stringify(this.createFields())) {
            this.selectedLanguage = key
          }
        })
      }
    },
    async handleImageUpload({ file, url }) {
      const formData = new FormData()
      formData.append('cover', file)

      const resp = await apiClient.post('/admin/custom-page/cover', formData)
      const previewId = resp.data?.data?.id

      if (!previewId) {
        const errorMessage =
          resp.data?.error?.message ||
          'Something went wrong while uploading header image'

        this.$notification.error({
          message: errorMessage,
        })
      } else {
        this.currentForm.backgroundImageFile = file
        this.currentForm.backgroundImageId = previewId
        this.currentForm.backgroundImageUrl = url
      }

      this.$refs.widgetForm.validateField('backgroundImageUrl')
    },
    getPopupContainer() {
      return document.body
    },
    handleTemplateChange(newTemplate) {
      const filtered = (newTemplate || []).filter(
        (template) => template !== '' && template !== undefined,
      )
      const last = filtered[filtered.length - 1] || []
      this.currentForm.link = last
    },
    deleteTemplate(event, templateId) {
      event.stopPropagation()
      console.log(templateId)
    },
    openAddLinkModalModal(linkObj) {
      if (this.loading) {
        return
      }

      this.selectedLink = { ...linkObj }
      this.isAddLinkModalOpened = true
    },
    async handleAddTemplatesModal(newTemplate) {
      this.isAddLinkModalOpened = false
      this.linkTemplates.push(newTemplate)
    },
    async getLinkTemplates() {
      this.loading = true
      const url = '/admin/links?type=custom&results=1000'
      try {
        const response = await apiClient.get(url)
        this.linkTemplates = response.data.data
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.loading = false
      }
    },
    handleSubmit() {
      this.$refs.widgetForm.validate((valid) => {
        if (valid) {
          const filteredForm = Object.fromEntries(
            Object.entries(this.form).filter(
              ([key, val]) =>
                JSON.stringify(val) !== JSON.stringify(FORM_FIELDS),
            ),
          )

          this.$emit(
            'submit',
            filteredForm,
            this.isEditMode ? this.editId : null,
          )
        } else {
          return false
        }
      })
    },
    handleCopyFromDefault() {
      const defaultForm = this.form[DEFAULT_LANG]

      this.form[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    clearValidation(fields) {
      if (this.$refs.widgetForm) {
        this.$refs.widgetForm.clearValidate(fields)
      }
    },
    validateForm() {
      if (!this.$refs.widgetForm) return
      this.$refs.widgetForm.validate((valid) => {
        this.validationResult = valid
      })
    },
    handleClearLang(lang) {
      this.form[lang] = { ...FORM_FIELDS }

      this.$refs.widgetForm.clearValidate()
    },
  },
  computed: {
    ...mapState({
      langs: (state) => state.langs.langData,
      sports: (state) => state.customPages.sports,
    }),
    currentForm() {
      return this.form[this.selectedLanguage]
    },
    isLinkModalDisabled() {
      return this.form?.link?.length === 0
    },
    isEditMode() {
      return this.data && !!Object.entries(this.data).length
    },
    editId() {
      if (!this.isEditMode) {
        return null
      }

      return Object.values(this.form).filter((lang) => lang.id)?.[0].id
    },
    filteredLinkTemplates() {
      const uniqueTemplates = new Set()
      return this.linkTemplates.filter((template) => {
        const url = template.url.trim()
        if (!url || uniqueTemplates.has(url)) {
          return false
        }
        uniqueTemplates.add(url)
        return true
      })
    },
    isInitialForm() {
      return JSON.stringify(this.currentForm) === JSON.stringify(FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.langs.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.form[name], FORM_FIELDS)
        return obj
      }, {})
    },
  },
  watch: {
    'currentForm.linkType': {
      handler(val) {
        if (!this.currentForm || isEqual(this.currentForm, this.data?.[this.selectedLanguage])) return

        this.currentForm.link = val === 'custom' ? [] : null
        this.clearValidation(['link', 'sportId'])
      },
      immediate: true,
    },
    currentForm: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
  created() {
    this.initForm()

    this.fetchSports()
    this.getLinkTemplates()

    this.fillForm()

    this.selectedLanguage = this.initLang
  },
}
</script>
<style lang="scss" scoped>
.widget-modal {
  position: relative;

  ::v-deep .ant-form-item-label {
    overflow: visible;
  }

  ::v-deep .ant-tooltip {
    font-weight: normal;
  }

  ::v-deep .ant-form-item {
    margin-bottom: 16px;
  }

  ::v-deep .ant-form-item-control::before,
  ::v-deep .ant-form-item-control::after {
    content: none;
  }
}

.switch {
  display: flex;
  align-items: center;

  ::v-deep label {
    margin: 0;
  }

  ::v-deep .ant-form-item-control {
    line-height: 1;
  }
}

.image-uploader {
  position: relative;
  max-width: 445px;
  margin-top: 18px;

  & ::v-deep .ant-upload-select {
    width: 100%;
    height: 100%;
  }

  & ::v-deep .anticon {
    font-size: 24px !important;
  }
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 16px;
  border: 1px dashed #d9d9d9;
  text-align: center;
  cursor: pointer;
  background: #fafafa;

  .ant-upload-hint {
    font-size: 14px;
    line-height: 22px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  ::v-deep .has-error & {
    border-color: #f5222e;
  }
}

.upload-preview {
  position: relative;
  display: block;
  width: 100%;

  img {
    width: 100%;
  }
}

.form-row {
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;

  & > div {
    min-height: 0;
    min-width: 0;
  }
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
